"use client";
import useTranslate from "@app/_hooks/useTranslate";
import type { Sort } from "types/common";

import {
  PRODUCTS_PER_PAGE,
  TOTAL_PRODUCTS_WITH_EAGER_LOADING,
} from "@/config/products";
import { cn } from "@/lib/utils";
import type { MarketAwareSlug } from "@/utils/const";
import useSortingOptions from "@/utils/hooks/useSortingOptions";
import ActiveFilters from "@app/_blocks/CategoryPageComponents/activeFilters";
import Filter from "@app/_blocks/CategoryPageComponents/Filter";
import SortBy from "@app/_blocks/CategoryPageComponents/sortBy";
import { Pagination } from "@app/_components/Pagination";
import ProductCard from "@app/_components/ProductCard";
import { useMarket } from "@app/_context";
import type { LandingPageBody, LandingPageParams } from "@apptus/esales-api";
import { Button, Dialog } from "@components/ngg";
import type {
  BrandPageQuery,
  CampaignFragment,
  CategoryPageQuery,
  DesignerPageQuery,
} from "@server/gql/graphql";
import useProductListing from "@server/hooks/useProductListing";
import type getLandingPage from "@server/requests/elevate/getLandingPage";
import { useMemo, useState } from "react";

type Props = {
  data: Awaited<ReturnType<typeof getLandingPage>>;
  page:
    | BrandPageQuery["brand"]
    | CampaignFragment
    | CategoryPageQuery["page"]
    | DesignerPageQuery["designer"];
  body?: LandingPageBody;
  params: LandingPageParams;
  details: MarketAwareSlug;
  sort?: Sort;
};

const CloseButton = (onClose: () => void, label: string) => (
  <button type="button" onClick={onClose} className="uppercase underline">
    {label}
  </button>
);

const ClearAll = (onClick: () => void, label: string) => (
  <button
    type="button"
    onClick={onClick}
    className="mr-3 ml-auto uppercase underline"
  >
    {label}
  </button>
);

export default function ProductList(props: Props) {
  const t = useTranslate();
  const { params, body, data: initialData, details, sort: defaultSort } = props;
  const { formatter } = useMarket().state;
  const { data, state, updateFilter, updateSort } = useProductListing({
    initialData,
    pageNumber: details.pageNumber,
    params,
    body,
  });
  const sortingOptions = useSortingOptions();

  const [mobileFilterMenuOpen, setMobileFilterMenuOpen] = useState(false);

  const clearFilter = () => {
    if (!data?.filter) return;

    const filter = [...data.filter];
    const clearedFilter = filter.map((facet) => {
      const item = facet;
      item.values = [];
      return item;
    });
    updateFilter(clearedFilter);
  };

  const selectedFilters = useMemo(() => {
    if (!data?.filter) return 0;
    return data.filter.reduce((acc, facet) => {
      if (facet.type !== "checkbox") return acc;
      if (!facet?.values?.length) return acc;
      return acc + facet.values.length;
    }, 0);
  }, [data?.filter]);

  return (
    <>
      <div className="flex scroll-mt-[var(--header-height)] justify-between border-y">
        <div className="flex flex-1">
          <p className="flex h-10 shrink-0 items-center pl-3">
            {t("nrOfProducts", {
              TOTAL: formatter.format(data?.count ?? 0),
            })}
          </p>
        </div>

        <button
          type="button"
          className="flex h-10 items-center px-3 tracking-wide lg:hidden"
          onClick={() => setMobileFilterMenuOpen(true)}
        >
          <span>{t("filter")}</span>
          {selectedFilters > 0 ? (
            <span className="ml-1 bg-black px-1 text-white tabular-nums">
              {selectedFilters}
            </span>
          ) : null}
        </button>

        {/* Mobile filter menu */}
        <Dialog
          open={mobileFilterMenuOpen}
          onClose={() => setMobileFilterMenuOpen(false)}
          title={t("filter")}
          dialogTransitionProps={{
            enter: "ease-out duration-150",
            enterFrom: "-translate-x-full",
            enterTo: "translate-x-0",
            leave: "ease-in duration-150",
            leaveFrom: "translate-x-0",
            leaveTo: "-translate-x-full",
          }}
          classNames={{
            backdrop: "bg-black/70",
            panel:
              "h-full flex flex-col rounded-none m-0 py-0 max-w-[326px] sm:max-w-[85%] text-left w-full mr-auto",
            panelHeader:
              "flex justify-between border-b border-gray-150 p-3 text-sm font-normal uppercase",
          }}
          SubtitleComponent={() => ClearAll(clearFilter, t("resetAll"))}
          CloseButton={() =>
            CloseButton(() => setMobileFilterMenuOpen(false), t("close"))
          }
        >
          {data?.filter?.length ? (
            <div className="grow overflow-y-auto">
              <Filter
                // initialFilter={initialData.filter}
                filter={data.filter}
                onChange={updateFilter}
              />
            </div>
          ) : null}
          <div className="border-gray-150 border-t px-3 py-4">
            <Button
              fullWidth
              className="btn-primary py-3 text-base"
              onClick={() => setMobileFilterMenuOpen(false)}
            >
              {t("showProducts")} ({data?.count})
            </Button>
          </div>
        </Dialog>

        <SortBy
          value={state?.sort}
          initialValue={defaultSort}
          options={sortingOptions}
          onChange={updateSort}
        />
      </div>
      <div className="grid scroll-mt-[var(--header-height)] grid-cols-12 items-start empty:hidden">
        <div
          className={cn(
            "col-span-2 -mb-px hidden shrink-0 self-stretch lg:block",
          )}
        >
          <Filter
            slug={props.page?.slug ?? "root"}
            filter={data?.filter ?? []}
            onChange={updateFilter}
          />
        </div>

        <div
          className="col-span-full lg:col-span-10"
          data-testid="product-category-grid"
        >
          <ActiveFilters data={data} updateFilter={updateFilter} />
          <ul className="grid gap-px max-md:grid-cols-2 md:max-lg:grid-cols-3 lg:grid-cols-4">
            {data?.items.map((product, index) => (
              <li key={product.id}>
                <ProductCard.Root
                  key={`${product.id}-${index}`}
                  product={product}
                  // pageData={pageData}
                  isEager={index < TOTAL_PRODUCTS_WITH_EAGER_LOADING}
                >
                  <ProductCard.Link>
                    <ProductCard.Image>
                      <ProductCard.Badges />
                    </ProductCard.Image>
                    <ProductCard.Content />
                  </ProductCard.Link>
                </ProductCard.Root>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Pagination
        slug={details.cleanSlug}
        page={details.pageNumber}
        state={state}
        totalPages={data?.count ? Math.ceil(data.count / PRODUCTS_PER_PAGE) : 0}
      />
    </>
  );
}
